import React, { useEffect, useLayoutEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Pagination, PaginationItem } from 'reactstrap';
import MaterialTable from 'material-table';
import { change, Field, reduxForm } from 'redux-form';
import { withTranslation } from 'react-i18next';
import AddIcon from '@material-ui/icons/Add';
import { IconButton, Tooltip } from '@material-ui/core';
import GraphQL from '../../../GraphQL';
import {
  changeLoadingTrue,
  changeLoadingFalse,
} from '../../../redux/actions/loadingActions';
import { showNotificationAutoDestroy } from '../../../shared/components/ShowNotification';
import DateField from '../../../shared/components/form/DateField';
import renderField from '../../../shared/components/form/RenderField';
import Select from '../../../shared/components/form/Select';
import {
  currencyMasker,
  percentageMasker,
} from '../../../shared/components/form/Masks';
import Modal from '../../../shared/components/Modal';
import columns from './ColumnsProduct';
import {
  productListByFilter,
  paymentFrequencyListByFilter,
} from '../../../shared/queries/Contract';
import materialTableIcons from '../../../shared/utils/materialTableIcons';
import MaterialTableThemeProvider from '../../../shared/components/MaterialTableThemeProvider';
import SelectVirtualized from '../../../shared/components/form/SelectVirtualized';

const ProductTable = (props) => {
  const { t, initialize, dispatch, data, titleId } = props;

  const [products, setProducts] = useState([]);
  const [itemDelete, setItemDelete] = useState([]);
  const [frequencyList, setFrequencyList] = useState([]);
  const [productList, setProductList] = useState([]);
  const [modal, setModal] = useState(false);
  const [clearFieldsProduct, setClearFieldsProduct] = useState(false);
  const [total, setTotal] = useState(0);
  const [itemProduct, setItemProduct] = useState({
    quantity: 1,
    unitaryValue: 0,
    amount: 0,
    discountPercentage: 0,
    discountAmount: 0,
    firstDueDate: new Date().getTime(),
  });

  const showLoading = () => {
    dispatch(changeLoadingTrue());
  };

  const hideLoading = () => {
    dispatch(changeLoadingFalse());
  };

  const toggleModal = () => {
    setModal(!modal);
  };

  const loadAllProducts = () => {
    const query = productListByFilter();
    showLoading();
    GraphQL(query, {})
      .then((res) => {
        res.productListByFilter = res.productListByFilter.map((item) => ({
          ...item,
          label: item.name,
        }));
        setProductList(res.productListByFilter);
        hideLoading();
      })
      .catch(() => {
        hideLoading();
        showNotificationAutoDestroy(
          'danger',
          t('errors.title'),
          t('errors.error-to-loading')
        );
      });
  };

  const loadFrequencyList = () => {
    const query = paymentFrequencyListByFilter();
    showLoading();
    GraphQL(query, {})
      .then((res) => {
        res.paymentFrequencyListByFilter = res.paymentFrequencyListByFilter.map(
          (item) => ({
            ...item,
            value: item.id,
            label: item.name,
          })
        );
        setFrequencyList(res.paymentFrequencyListByFilter);
        hideLoading();
      })
      .catch(() => {
        hideLoading();
        showNotificationAutoDestroy(
          'danger',
          t('errors.title'),
          t('errors.error-to-loading')
        );
      });
  };

  const itemProductInputChange = (evt) => {
    if (evt) {
      const updatedItemProd = { ...itemProduct };
      updatedItemProd[evt.target.name] = evt.target.value;
      setItemProduct(updatedItemProd);
    }
  };

  const itemProductChange = (field, value) => {
    const updatedItemProd = { ...itemProduct };
    updatedItemProd[field] = value;
    setItemProduct(updatedItemProd);
  };

  const onAddProduct = () => {
    if (!itemProduct.product) {
      showNotificationAutoDestroy(
        'danger',
        t('notifications.attention'),
        t('notifications.select-product')
      );
    } else if (!itemProduct.numberOfInstallments) {
      showNotificationAutoDestroy(
        'danger',
        t('notifications.attention'),
        t('notifications.enter-installments')
      );
    } else if (!itemProduct.paymentFrequency) {
      showNotificationAutoDestroy(
        'danger',
        t('notifications.attention'),
        t('notifications.select-frequency')
      );
    } else {
      const updatedProducts = [...products, itemProduct];
      setProducts(updatedProducts);
      setClearFieldsProduct(!clearFieldsProduct);
    }
  };

  const onAmountChange = (updatedItemProd) => {
    const updated = { ...updatedItemProd };
    const { quantity, unitaryValue, discountPercentage } = updated;
    const discountAmount = (quantity * unitaryValue * discountPercentage) / 100;
    const amount = quantity * unitaryValue - discountAmount;
    updated.discountAmount = discountAmount;
    updated.amount = amount;
    setItemProduct(updated);
  };

  const onDiscountChange = (value) => {
    const updatedItemProd = { ...itemProduct };
    let valueFormat = value.replace(',', '.');
    valueFormat = parseFloat(valueFormat);
    if (valueFormat >= 0 && valueFormat <= 100) {
      updatedItemProd.discountPercentage = valueFormat;
    } else {
      updatedItemProd.discountPercentage = 0;
    }
    onAmountChange(updatedItemProd);
  };

  const onQuantityChange = (qt) => {
    const updatedItemProd = { ...itemProduct };
    const intValue = parseInt(qt, 10);
    if (intValue > 0 && intValue < 99) {
      updatedItemProd.quantity = intValue;
      onAmountChange(updatedItemProd);
    } else {
      setItemProduct({ ...itemProduct });
      showNotificationAutoDestroy(
        'danger',
        t('notifications.attention'),
        t('notifications.select-valid-quantity')
      );
    }
  };

  const onUnitaryChange = (value) => {
    const updatedItemProd = { ...itemProduct };
    const replaceCcy = value.replace(/[R$]/g, '');
    const replaceDots = replaceCcy.replace(/[.]/g, '');
    const replaceComma = replaceDots.replace(',', '.');
    const unitValue = parseFloat(replaceComma);
    updatedItemProd.unitaryValue = unitValue;
    onAmountChange(updatedItemProd);
  };

  const onSelectProductChange = (values) => {
    const updatedItemProd = { ...itemProduct };
    updatedItemProd.product = values;
    updatedItemProd.unitaryValue = values.value;
    updatedItemProd.category = values.category;
    onAmountChange(updatedItemProd);
  };

  const deleteItemProduct = () => {
    const updatedProducts = [...products];
    const index = updatedProducts.findIndex(
      (item) => item.tableData.id === itemDelete.tableData.id
    );
    updatedProducts.splice(index, 1);
    setProducts(updatedProducts);
    toggleModal();
  };

  const calculateTotal = (values) => {
    const totalAmount = values.reduce((int, item) => {
      return int + item.amount;
    }, 0);
    setTotal(
      totalAmount.toLocaleString('pt-br', {
        style: 'currency',
        currency: 'BRL',
      })
    );
  };

  useEffect(() => {
    loadAllProducts();
    loadFrequencyList();
    setProducts(data);
    return () => {
      setProductList([]);
    };
  }, []);

  useEffect(() => {
    initialize(itemProduct);
  }, [itemProduct]);

  useEffect(() => {
    dispatch(change('ContractForm', 'products', products));
    calculateTotal(products);
  }, [products]);

  useLayoutEffect(() => {
    setItemProduct({
      quantity: 1,
      unitaryValue: 0,
      amount: 0,
      discountPercentage: 0,
      discountAmount: 0,
      firstDueDate: new Date().getTime(),
    });
  }, [clearFieldsProduct]);

  return (
    <>
      <Modal
        color="warning"
        title={t('notifications.attention')}
        btn="Warning"
        message={t('notifications.delete_modal')}
        toggle={toggleModal}
        modal={modal}
        onConfirm={() => deleteItemProduct('ww')}
      />
      {titleId === 0 && (
        <>
          <div className="form__form-group">
            <span className="form__form-group-label">
              {t('general.product')}
            </span>
            <div className="form_form-group-field">
              <Field
                name="product"
                type="text"
                component={SelectVirtualized}
                isClearable={false}
                options={productList}
                onChange={(values) => onSelectProductChange(values)}
                placeholder={t('screens.mnt_contratos.placeholders.select')}
              />
            </div>
          </div>
          <div className="form__form-group--4_columns">
            <div className="form__form-group">
              <span className="form__form-group-label">
                {t('general.amount')}
              </span>
              <div className="form__form-group-field">
                <Field
                  name="quantity"
                  type="number"
                  component={renderField}
                  min="1"
                  max="99"
                  onBlur={(evt) => onQuantityChange(evt.target.value)}
                />
              </div>
            </div>
            <div className="form__form-group">
              <span className="form__form-group-label">
                {t('general.unit-value')}
              </span>
              <div className="form__form-group-field">
                <Field
                  name="unitaryValue"
                  type="text"
                  maxLength="14"
                  component={renderField}
                  onBlur={(evt) => onUnitaryChange(evt.target.value)}
                  {...currencyMasker()}
                />
              </div>
            </div>
            <div className="form__form-group">
              <span className="form__form-group-label">
                {t('general.discount')}
              </span>
              <div className="form__form-group-field">
                <Field
                  name="discountPercentage"
                  type="text"
                  component={renderField}
                  onBlur={(evt) => onDiscountChange(evt.target.value)}
                  {...percentageMasker()}
                />
              </div>
            </div>
            <div className="form__form-group">
              <span className="form__form-group-label">
                {t('general.quota')}
              </span>
              <div className="form__form-group-field">
                <Field
                  name="numberOfInstallments"
                  type="number"
                  component={renderField}
                  min="1"
                  max="99"
                  onBlur={(evt) => itemProductInputChange(evt)}
                />
              </div>
            </div>
          </div>
          <div className="form__form-group">
            <span className="form__form-group-label">
              {t('general.payment-freq')}
            </span>
            <div className="form_form-group-field">
              <Field
                name="paymentFrequency"
                type="text"
                component={Select}
                options={frequencyList}
                onChange={(value) =>
                  itemProductChange('paymentFrequency', value)
                }
                placeholder={t('screens.mnt_contratos.placeholders.select')}
              />
            </div>
          </div>
          <div className="form__form-group--3_columns">
            <div className="form__form-group">
              <span className="form__form-group-label">
                {t('general.due-date-day')}
              </span>
              <div className="form__form-group-field">
                <Field
                  name="firstDueDate"
                  component={DateField}
                  selected={new Date()}
                  onChange={(value) =>
                    itemProductChange('firstDueDate', value.getTime())
                  }
                />
              </div>
            </div>
            <div className="form__form-group--2_columns d-flex flex-grow-1">
              <div className="form__form-group">
                <span className="form__form-group-label">
                  {t('general.subtotal')}
                </span>
                <div className="form__form-group-field">
                  <Field
                    name="amount"
                    type="text"
                    readOnly
                    component={renderField}
                    {...currencyMasker()}
                  />
                </div>
              </div>
              <div className="form__form-group-field flex-1">
                <Tooltip title={t('general.add-product')}>
                  <IconButton
                    aria-label="add"
                    onClick={(values) => onAddProduct(values)}
                  >
                    <AddIcon />
                  </IconButton>
                </Tooltip>
              </div>
            </div>
          </div>
        </>
      )}
      <MaterialTableThemeProvider>
        <div className="form__form-group">
          <MaterialTable
            data={products}
            icons={materialTableIcons}
            columns={columns(t)}
            actions={
              titleId === 0 && [
                {
                  icon: 'delete',
                  tooltip: t('general.tables.delete_registry'),
                  onClick: (evt, rowData) => {
                    setItemDelete(rowData);
                    toggleModal();
                  },
                },
              ]
            }
            options={{
              paging: false,
              showTitle: false,
              search: false,
              toolbar: false,
              actionsColumnIndex: 9,
              headerStyle: {
                zIndex: 0,
              },
            }}
            localization={{
              header: {
                actions: t('general.tables.actions'),
              },
              body: {
                emptyDataSourceMessage: t('general.tables.no-data-to-display'),
              },
            }}
          />
          <Pagination className="form__form-group d-flex mt-4">
            <PaginationItem className="form__form-group-label">
              {t('general.total')}
            </PaginationItem>
            <PaginationItem className="h5 ml-2 text-success">
              {total}
            </PaginationItem>
          </Pagination>
        </div>
      </MaterialTableThemeProvider>
    </>
  );
};

ProductTable.propTypes = {
  t: PropTypes.func.isRequired,
  initialize: PropTypes.func.isRequired,
  dispatch: PropTypes.func.isRequired,
  data: PropTypes.arrayOf(PropTypes.shape(PropTypes.shape)).isRequired,
  titleId: PropTypes.number.isRequired,
};

export default reduxForm({
  form: 'ProductTableForm',
  keepDirtyOnReinitialize: true,
  enableReinitialize: true,
})(withTranslation('common')(ProductTable));
